import { GoogleGenAI, GenerateContentResponse } from "@google/genai";
import { withRetry } from "./aiUtilities";
import { getProxyAuthHeaders } from "./dataFetchers";
import { MODELS } from "../config/models";
import type { ModelEntry } from "../config/models";
export const getEnvKey = () => typeof process !== 'undefined' ? process.env.GEMINI_API_KEY : undefined;
let _aiInstance: GoogleGenAI | null = null;
export function getAiInstance(): GoogleGenAI {
  if (!_aiInstance) {
    _aiInstance = new GoogleGenAI({ apiKey: (getEnvKey() || "") as string });
  }
  return _aiInstance;
}

// ─── Token / model instrumentation (measurement only — ships free) ───────────
// A per-analysis ledger that accumulates token usage and the actual model
// version Google returned, keyed by agent. Created once per analysis and
// threaded into the agent runners; callGemini / callGeminiWithTools record into
// it after each call. Reading is additive and never alters request behavior.
export interface AgentUsage {
  prompt: number;
  output: number;
  thought: number;
  total: number;
}
export interface TokenLedger {
  tokens: Record<string, AgentUsage>;
  /** Actual modelVersion string Google returned per agent (last call wins). */
  modelVersions: Record<string, string>;
}
export interface GeminiTelemetry {
  ledger?: TokenLedger;
  agent?: string;
}
export function createTokenLedger(): TokenLedger {
  return { tokens: {}, modelVersions: {} };
}
/**
 * Accumulate one response's usageMetadata + modelVersion onto the ledger under
 * `agent`. No-op unless a ledger and agent are both supplied. Token counts sum
 * across calls (so a tool-circulation loop or a fallback retry aggregates
 * correctly); the modelVersion is overwritten with the most recent call's value.
 */
export function recordUsage(
  ledger: TokenLedger | undefined,
  agent: string | undefined,
  resp: any,
): void {
  if (!ledger || !agent || !resp) return;
  const u = resp.usageMetadata;
  if (u) {
    const cur = ledger.tokens[agent] || { prompt: 0, output: 0, thought: 0, total: 0 };
    cur.prompt += u.promptTokenCount ?? 0;
    cur.output += u.candidatesTokenCount ?? 0;
    cur.thought += u.thoughtsTokenCount ?? 0;
    cur.total += u.totalTokenCount ?? 0;
    ledger.tokens[agent] = cur;
  }
  if (typeof resp.modelVersion === "string" && resp.modelVersion) {
    ledger.modelVersions[agent] = resp.modelVersion;
  }
}

/**
 * The single Gemini-3 config sanitizer. Filters the config down to the keys the
 * SDK accepts and applies the agent's service tier. Previously this same block
 * was duplicated in callGemini, callGeminiWithTools, and the /api/gemini proxy;
 * all three now call this.
 *
 * [MODEL-1] The tier is applied PER-ENTRY from the registry: only agents whose
 * entry declares the priority tier (C, B, D, E, Oracle sub-call) run on it;
 * lite/background roles (no tier) run on the default (standard) tier. The line
 * below is the single place the tier is set outside the registry.
 */
export function buildGeminiConfig(model: string, config: any, entry?: ModelEntry): any {
  const validGemini3Keys = [
    "thinking_level", "tools", "toolConfig", "systemInstruction",
    "temperature", "maxOutputTokens", "stopSequences", "responseMimeType",
    "responseSchema", "candidateCount", "mediaResolution",
  ];
  const sanitizedConfig: any = {};
  for (const key of Object.keys(config || {})) {
    if (validGemini3Keys.includes(key)) {
      sanitizedConfig[key] = config[key];
    }
  }
  if (entry?.serviceTier) sanitizedConfig.serviceTier = entry.serviceTier;
  return sanitizedConfig;
}

export async function callGemini(params: { model?: string, contents: any, config?: any, telemetry?: GeminiTelemetry, entry?: ModelEntry }): Promise<GenerateContentResponse> {
  const primaryModel = params.model || MODELS.ORACLE.primary;
  const hasLocalKey = !!(getEnvKey() || "").trim();

  return await withRetry(async () => {
    if (hasLocalKey) {
      try {
        const config = { ...params.config };
        if (config.tools) {
          config.toolConfig = { ...config.toolConfig, includeServerSideToolInvocations: true };
        }

        const sanitizedConfig = buildGeminiConfig(primaryModel, config, params.entry);
        const resp = await getAiInstance().models.generateContent({
          model: primaryModel,
          contents: params.contents,
          config: sanitizedConfig
        });
        recordUsage(params.telemetry?.ledger, params.telemetry?.agent, resp);
        return resp;
      } catch (err: any) {
        // [MODEL-1] All registry fallbacks are now Gemini-3 models that support
        // tool circulation + thinking_level, so the fallback no longer strips
        // tools/thinking — it uses the single buildGeminiConfig sanitizer, same
        // as the primary. The maxOutputTokens bump is kept for headroom.
        const fallbackModel = params.entry?.fallback ?? MODELS.ORACLE.fallback;
        console.warn(`[Gemini] Primary model (${primaryModel}) failed, attempting fallback to ${fallbackModel}...`, err);

        const config = { ...params.config };
        if (config.tools) {
          config.toolConfig = { ...config.toolConfig, includeServerSideToolInvocations: true };
        }
        config.maxOutputTokens = Math.max(config.maxOutputTokens || 8192, 16384);

        const sanitizedConfig = buildGeminiConfig(fallbackModel, config, params.entry);
        const resp = await getAiInstance().models.generateContent({
          model: fallbackModel,
          contents: params.contents,
          config: sanitizedConfig
        });
        recordUsage(params.telemetry?.ledger, params.telemetry?.agent, resp);
        return resp;
      }
    } else {
      // Use the proxy if no local key exists
      const resp = await callGeminiViaProxy({
        model: primaryModel,
        contents: params.contents,
        config: params.config
      });
      recordUsage(params.telemetry?.ledger, params.telemetry?.agent, resp);
      return resp;
    }
  });
}
export async function callGeminiViaProxy(params: {
  model?: string,
  contents: any,
  config?: any
}, retries = 10, backoffMs = 3000): Promise<GenerateContentResponse> {
  let response;
  try {
    let absoluteUrl = "/api/gemini";
    if (typeof window === 'undefined') {
      absoluteUrl = `http://127.0.0.1:3000${absoluteUrl}`;
    }
    
    // Create an explicit AbortController to cleanly abort fetch if it hangs.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1800000); // 30m

    response = await fetch(absoluteUrl, {
      method: "POST",
      // [KEEP2] /api/gemini now requires auth and keys the rate limiter by uid.
      headers: { "Content-Type": "application/json", ...(await getProxyAuthHeaders()) },
      body: JSON.stringify({
        model: params.model || MODELS.ORACLE.primary,
        contents: params.contents,
        config: params.config
      }),
      signal: controller.signal
    });
    clearTimeout(timeoutId);
  } catch (error: any) {
    if (error.name === 'AbortError') {
      console.log(`[Gemini Proxy] Local fetch aborted after 30m timeout. Retrying...`);
    } else {
      console.log(`Fetch error, retrying in 3s... (${retries} left)`, error);
    }
    if (retries > 0) {
      await new Promise(r => setTimeout(r, 3000));
      return callGeminiViaProxy(params, retries - 1);
    }
    throw error;
  }

  const contentType = response.headers.get("content-type");
  const isHtml = contentType && contentType.includes("text/html");

  if (!response.ok) {
    // [KEEP2] Our own proxy limiter (429 with code GEMINI_RATE_LIMIT) must NOT be
    // retried — retrying against our own window only burns it. Fail fast and
    // carry retryAfterSeconds up to the UI. Upstream 503/429 (numeric code) still
    // go through the backoff below.
    if (response.status === 429) {
      const body = await response.clone().json().catch(() => null) as any;
      if (body?.code === 'GEMINI_RATE_LIMIT') {
        const headerRetry = Number(response.headers.get('Retry-After'));
        const err: any = new Error(body.error || 'Analysis rate limit reached.');
        err.status = 429;
        err.code = 'GEMINI_RATE_LIMIT';
        err.retryAfterSeconds = body.retryAfterSeconds ?? (Number.isFinite(headerRetry) ? headerRetry : undefined);
        throw err;
      }
    }
    if ((response.status >= 500 || isHtml) && retries > 0) {
      const text = await response.clone().text().catch(() => "");
      const isJson = contentType && contentType.includes("application/json");

      if (isJson && (response.status === 503 || response.status === 429)) {
        // Exponential backoff — avoids hammering Gemini during a high-demand event.
        // Sequence: 3s → 6s → 12s → 24s → 30s (capped). Jitter prevents thundering herd.
        const jitter = Math.random() * 1000;
        const nextBackoff = Math.min(backoffMs * 2, 30000);
        console.log(`Gemini API overloaded (${response.status}), retrying in ${Math.round((backoffMs + jitter) / 1000)}s... (${retries} left)`);
        await new Promise(r => setTimeout(r, backoffMs + jitter));
        return callGeminiViaProxy(params, retries - 1, nextBackoff);
      }

      if (text.includes("Starting Server")) {
        console.log(`Server environment is still starting up, retrying in 3s... (${retries} left)`);
        await new Promise(r => setTimeout(r, 3000));
        return callGeminiViaProxy(params, retries - 1);
      } else if (response.status >= 500) {
        console.log(`Transient API error (${response.status}), retrying in 3s... (${retries} left).`);
        await new Promise(r => setTimeout(r, 3000));
        return callGeminiViaProxy(params, retries - 1);
      }
    }
    
    const isJson = contentType && contentType.includes("application/json");
    const err = isJson ? await response.json().catch(() => ({ error: response.statusText })) : { error: response.statusText };
    const errorObj: any = new Error(err.error || `Proxy error: ${response.status}`);
    errorObj.status = err.code || response.status;
    throw errorObj;
  }

  if (contentType && !contentType.includes("application/json")) {
    const text = await response.text();
    if (text.includes("Starting Server") && retries > 0) {
       console.log(`Server is still starting (200 OK HTML), retrying in 3s... (${retries} left)`);
       await new Promise(r => setTimeout(r, 3000));
       return callGeminiViaProxy(params, retries - 1);
    }
    console.error(`[Gemini Proxy] Unexpected content-type (${contentType}): ${text.substring(0, 200)}`);
    throw new Error(`Unexpected content-type: ${contentType}`);
  }
  
  try {
    const text = await response.text();
    if (!text || text.trim() === "") {
        throw new Error("Empty response body from proxy");
    }
    return JSON.parse(text);
  } catch (err: any) {
    console.error(`[Gemini Proxy] Failed to parse JSON. Error: ${err.message}`);
    throw err;
  }
}
export async function callServerAI(params: { model?: string, contents: any, config?: any, telemetry?: GeminiTelemetry, entry?: ModelEntry }): Promise<GenerateContentResponse> {
  // callGemini uses the Gemini SDK directly when GEMINI_API_KEY is set (all server-side contexts),
  // and falls back to the HTTP proxy only in browser environments where no local key is available.
  // Never use callGeminiViaProxy here — the /api/gemini proxy route is rate-limited and will
  // immediately reject server-originated self-calls.
  return callGemini(params);
}
